import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { BRANCHES, branchIdBySlug, branchNameById, STATUS_LABEL } from './speakupConstants';
import SpeakUpShell from './SpeakUpShell';
import './speakup.css';

export default function SpeakUpBoard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const branchSlug = searchParams.get('branch');
  const branchId = branchIdBySlug(branchSlug);

  const [items, setItems] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setItems(null);
    // Gate decision D3: a null branch now means EVERYTHING published, not
    // company-wide only. That old default made a branch's own answers the
    // harder thing to find, which is backwards for a board whose whole job is
    // to show that saying something changed something.
    supabase.rpc('get_public_board', { p_branch_id: branchId }).then(({ data, error: rpcError }) => {
      if (rpcError) { setError('Could not load the board right now.'); return; }
      setItems(data || []);
    });
  }, [branchId]);

  function handleBranchChange(e) {
    const slug = e.target.value;
    if (slug) setSearchParams({ branch: slug });
    else setSearchParams({});
  }

  return (
    <SpeakUpShell>
        <h1 className="speakup-h1">What we've heard</h1>
        {/* Callout D2: the lede says what the board actually is, and stops
            attributing it to a department D&R does not have. */}
        <p className="speakup-lede">
          Ideas people raised, and what we did about them. Written up by us — never the original words,
          never a name.
        </p>

        <label className="speakup-label" style={{ marginTop: 0 }}>Branch</label>
        <select className="speakup-input" value={branchSlug || ''} onChange={handleBranchChange} style={{ marginBottom: 16 }}>
          <option value="">All branches</option>
          {BRANCHES.map((b) => <option key={b.id} value={b.slug}>{b.name}</option>)}
        </select>

        {error && <div className="speakup-error">{error}</div>}
        {items === null && !error && <div className="speakup-note">Loading…</div>}

        {/* Callout D4: the empty state has to read as "nothing published yet"
            rather than "nobody listens here". */}
        {items && items.length === 0 && (
          <div className="speakup-empty">
            <strong>Nothing posted here yet.</strong>
            <div className="speakup-check-sub" style={{ marginTop: 6 }}>
              When an idea leads to a change, it gets written up here. Be the first —{' '}
              <Link to="/speak-up" className="speakup-inlinelink-a">share an idea</Link>.
            </div>
          </div>
        )}

        {/* Callout D1: two labelled halves. One text box read as a
            noticeboard; split, it reads as evidence that saying something
            changes something -- which is the only reason the board exists. */}
        {items && items.map((item) => (
          <div className="speakup-board-card" key={item.id}>
            {item.public_said ? (
              <>
                <div className="speakup-said-lbl">You said</div>
                <div className="speakup-said">{item.public_said}</div>
                <div className="speakup-said-lbl">We did</div>
                <div className="speakup-did">{item.public_summary}</div>
              </>
            ) : (
              /* Published before the split existed. Rendered as one block
                 rather than an empty "You said" -- the NULL is an honest
                 record of what was captured, not a gap to paper over. */
              <div className="speakup-did">{item.public_summary}</div>
            )}
            <div className="speakup-chip-row" style={{ marginTop: 12 }}>
              <span className="speakup-chip speakup-chip-cat">{item.category}</span>
              <span className="speakup-chip speakup-chip-status">{STATUS_LABEL[item.status] || item.status}</span>
              <span className="speakup-when">{branchNameById(item.branch_id)}</span>
            </div>
          </div>
        ))}

        <div style={{ marginTop: 20 }}>
          <Link to="/speak-up" className="speakup-back">← Back</Link>
        </div>
    </SpeakUpShell>
  );
}
